import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isActiveFeeRunStatus } from '@/features/fee-runs/fee-run-helpers';
import {
  approveFeeRun,
  cancelFeeRun,
  type FeeRunRequest,
  type FeeRunsQuery,
  type FeeRunUsersQuery,
  getFeeRun,
  getFeeRunUsers,
  listFeeRuns,
  requestFeeRun,
  retryFeeRun,
} from '@/services/api/fee-runs';

// The run header is one GetItem, so polling it every 2 s while it moves is cheap; the list and the users
// page are Queries, so they poll more slowly. Intervals are set per-query, never on a global default.
const FAST_POLL_INTERVAL_MS = 2_000;
const SLOW_POLL_INTERVAL_MS = 5_000;

export const FEE_RUNS_QUERY_KEY = ['operations', 'fee-runs'] as const;

export const feeRunsQueryOptions = (query: FeeRunsQuery = {}) =>
  queryOptions({
    queryKey: [...FEE_RUNS_QUERY_KEY, 'list', query.cursor ?? '', query.limit ?? 0],
    queryFn: () => listFeeRuns(query),
    placeholderData: keepPreviousData,
    // The list carries live statuses; refresh it while any listed run is still moving
    refetchInterval: (state) => (state.state.data?.runs.some((run) => isActiveFeeRunStatus(run.status)) ? SLOW_POLL_INTERVAL_MS : false),
  });

export const useFeeRuns = (query: FeeRunsQuery = {}) => useQuery(feeRunsQueryOptions(query));

export const feeRunQueryOptions = (runId: string) =>
  queryOptions({
    queryKey: [...FEE_RUNS_QUERY_KEY, 'run', runId],
    queryFn: () => getFeeRun(runId),
    // Poll failures are silent by design: a transient error must not toast every 2 s
    refetchInterval: (state) => (isActiveFeeRunStatus(state.state.data?.run.status) ? FAST_POLL_INTERVAL_MS : false),
  });

export const useFeeRun = (runId: string) => useQuery(feeRunQueryOptions(runId));

export const feeRunUsersQueryOptions = (runId: string, query: FeeRunUsersQuery, isRunActive: boolean) =>
  queryOptions({
    queryKey: [...FEE_RUNS_QUERY_KEY, 'run', runId, 'users', query.status ?? '', query.cursor ?? '', query.limit ?? 0],
    queryFn: () => getFeeRunUsers(runId, query),
    placeholderData: keepPreviousData,
    refetchInterval: isRunActive ? SLOW_POLL_INTERVAL_MS : false,
  });

export const useFeeRunUsers = (runId: string, query: FeeRunUsersQuery, isRunActive: boolean) => useQuery(feeRunUsersQueryOptions(runId, query, isRunActive));

const useInvalidateFeeRuns = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: FEE_RUNS_QUERY_KEY });
};

export const useRequestFeeRun = () => {
  const invalidate = useInvalidateFeeRuns();
  return useMutation({ mutationFn: (body: FeeRunRequest) => requestFeeRun(body), onSuccess: () => invalidate() });
};

export const useApproveFeeRun = (runId: string) => {
  const invalidate = useInvalidateFeeRuns();
  return useMutation({ mutationFn: (justification: string) => approveFeeRun(runId, justification), onSettled: () => invalidate() });
};

export const useCancelFeeRun = (runId: string) => {
  const invalidate = useInvalidateFeeRuns();
  return useMutation({ mutationFn: () => cancelFeeRun(runId), onSettled: () => invalidate() });
};

export const useRetryFeeRun = (runId: string) => {
  const invalidate = useInvalidateFeeRuns();
  return useMutation({ mutationFn: () => retryFeeRun(runId), onSettled: () => invalidate() });
};
